// La categoría del producto en "Editar producto", debajo del SKU
// (ADR-2026-10-21-categorias-propias-con-subcategorias,
// ADR-2026-10-22-una-categoria-por-producto-y-el-tipo-oculto).
//
// Es la de la marca (`categorias_tienda`, con subcategorías; el mismo árbol de los lanzamientos) y
// el producto va en UNA: una categoría o una subcategoría. Elegir "Oversize" ya es estar en
// "Remeras › Oversize". El "Tipo" de la IA (`categoria_producto`) no se muestra.
//
// El menú va portaleado (`PopoverAnclado`, capa 200): el modal es `fixed` z-61 y un `absolute`
// adentro se recortaría.
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Pencil, Plus, Search, X } from "lucide-react";
import { MenuDesplegable } from "./MenuDesplegable";
import {
  BotonDePlegar,
  LineasDeLaFila,
} from "../pages/lanzamientos/Categorias";
import {
  aplanarArbolConGuias,
  hermanaRepetida,
  partesDelCamino,
  resolverCamino,
} from "../pages/lanzamientos/arbolDeCategorias";
import type { CategoriaDeTienda } from "../pages/lanzamientos/lanzamientosApi";
import type { DetallesDelProducto } from "../lib/detallesDelProducto";
import { normalizarParaBuscar } from "../lib/busquedaDeProductos";

const disparador =
  "flex h-[var(--btn-h)] w-full items-center gap-2 rounded-full border border-black/[0.08] bg-white pl-4 pr-3 text-left text-[length:var(--text-body)] text-[#16181D] shadow-[0_1px_2px_rgba(16,24,40,0.04)] outline-none transition-shadow focus-visible:border-brand-primary focus-visible:ring-4 focus-visible:ring-[rgb(var(--brand-primary-rgb)_/_0.085)]";

/** Cuánto se corre cada nivel del árbol y el margen de la primera columna: los mismos del panel,
 *  así la línea guía cae debajo del comienzo del nombre de la madre. */
const NIVEL = 20;
const INICIO = 8;

/** "Remeras › Oversize" o "Remeras / Oversize" → las partes, para crear el camino entero. */
const partesEscritas = (texto: string): string[] =>
  texto
    .split(/\s*(?:›|>|\/)\s*/)
    .map((p) => p.trim())
    .filter(Boolean);

/** El camino de una categoría, con las madres más tenues y la última (la elegida) plena. */
const Camino: React.FC<{ partes: string[] }> = ({ partes }) => (
  <span className="min-w-0 truncate">
    {partes.slice(0, -1).map((p, i) => (
      <span key={i} className="text-black/45">
        {p}
        <span className="mx-1">›</span>
      </span>
    ))}
    <span>{partes[partes.length - 1]}</span>
  </span>
);

export const CampoDeCategoriaPropia: React.FC<{
  detalles: DetallesDelProducto | null;
  onChange: (d: DetallesDelProducto) => void;
  categorias: CategoriaDeTienda[];
  cargando: boolean;
  onCrear: (
    nombre: string,
    madre: string | null,
  ) => Promise<{ id: string | null; error: string | null }>;
  /** Abre el panel donde se renombran, mueven y borran. */
  onEditar: () => void;
}> = ({ detalles, onChange, categorias, cargando, onCrear, onEditar }) => {
  const id = useId();
  const ancla = useRef<HTMLButtonElement>(null);
  const [abierto, setAbierto] = useState(false);
  if (!detalles) return null;

  const elegida =
    detalles.categoriaPropia &&
    categorias.some((c) => c.id === detalles.categoriaPropia)
      ? detalles.categoriaPropia
      : null;
  const elegir = (cid: string | null) => {
    onChange({ ...detalles, categoriaPropia: cid });
    setAbierto(false);
  };

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between px-0.5">
        <label
          htmlFor={`${id}-cat`}
          className="text-[13px] font-semibold text-[#16181D]"
        >
          Categoría
        </label>
        <span className="text-[12.5px] text-black/40">Opcional</span>
      </div>
      <div className="relative">
        <button
          id={`${id}-cat`}
          ref={ancla}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={abierto}
          onClick={() => setAbierto((a) => !a)}
          className={`${disparador} ${elegida ? "pr-11" : ""}`}
        >
          <span className="flex min-w-0 flex-1">
            {elegida ? (
              <Camino partes={partesDelCamino(categorias, elegida)} />
            ) : (
              <span className="text-black/35">Elegí una categoría</span>
            )}
          </span>
          {!elegida && (
            <ChevronDown
              aria-hidden
              className={`h-4 w-4 shrink-0 text-black/45 transition-transform duration-200 ${abierto ? "rotate-180" : ""}`}
            />
          )}
        </button>
        {elegida && (
          <button
            type="button"
            onClick={() => onChange({ ...detalles, categoriaPropia: null })}
            title="Quitar la categoría"
            aria-label="Quitar la categoría"
            className="absolute inset-y-0 right-1.5 my-auto inline-flex h-8 w-8 items-center justify-center rounded-full text-black/40 transition-colors hover:bg-black/[0.04] hover:text-[#16181D]"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <MenuDesplegable
        abierto={abierto}
        anclaRef={ancla}
        onCerrar={() => setAbierto(false)}
        titulo="Categoría"
        ancho={Math.max(ancla.current?.offsetWidth ?? 0, 320)}
      >
        {(enTelefono) => (
          <MenuDeCategoria
            enfocarAlAbrir={!enTelefono}
            categorias={categorias}
            cargando={cargando}
            elegida={elegida}
            onElegir={elegir}
            onCrear={onCrear}
            onEditar={() => {
              setAbierto(false);
              onEditar();
            }}
          />
        )}
      </MenuDesplegable>
    </div>
  );
};

/**
 * El menú: buscador arriba (que también crea), el árbol en el medio y "Editar categorías" abajo.
 * Se elige UNA fila —categoría o subcategoría— y el menú se cierra. Cada fila tiene un "+" para
 * crearle una subcategoría ahí mismo, que queda elegida.
 *
 * Sin `onCrear` es sólo para elegir (el filtro de Mi marca —
 * ADR-2026-10-22-filtro-por-categoria-en-productos): busca pero no crea, y no hay "+". `extras` son
 * opciones fijas arriba del árbol ("Todas", "Sin categoría").
 */
export function MenuDeCategoria({
  categorias,
  cargando,
  elegida,
  onElegir,
  onCrear,
  onEditar,
  extras = [],
  etiquetaEditar = "Renombrar, mover o borrar categorías",
  enfocarAlAbrir = true,
}: {
  categorias: CategoriaDeTienda[];
  cargando: boolean;
  elegida: string | null;
  onElegir: (id: string) => void;
  onCrear?: (
    nombre: string,
    madre: string | null,
  ) => Promise<{ id: string | null; error: string | null }>;
  /** Sin esto no hay pie de "Editar": las categorías de la tienda se editan en la tienda. */
  onEditar?: () => void;
  extras?: Array<{ id: string; etiqueta: string }>;
  etiquetaEditar?: string;
  /** En la hoja del teléfono no: el teclado taparía la mitad del árbol apenas abre. */
  enfocarAlAbrir?: boolean;
}) {
  const [texto, setTexto] = useState("");
  // Con muchas, arranca plegado salvo el camino a la elegida; con pocas, todo abierto.
  const [plegadas, setPlegadas] = useState<Set<string>>(() => {
    if (categorias.length <= 20) return new Set();
    const abiertas = new Set<string>();
    const porId = new Map(categorias.map((c) => [c.id, c]));
    let madre = elegida ? (porId.get(elegida)?.parentId ?? null) : null;
    while (madre && !abiertas.has(madre)) {
      abiertas.add(madre);
      madre = porId.get(madre)?.parentId ?? null;
    }
    const conHijas = new Set(
      categorias.map((c) => c.parentId).filter((x): x is string => !!x),
    );
    return new Set([...conHijas].filter((x) => !abiertas.has(x)));
  });
  const [subcategoriaEn, setSubcategoriaEn] = useState<string | null>(null);
  const [nombreNueva, setNombreNueva] = useState("");
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const buscador = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (enfocarAlAbrir) buscador.current?.focus();
  }, [enfocarAlAbrir]);

  const filas = useMemo(
    () => aplanarArbolConGuias(categorias, plegadas),
    [categorias, plegadas],
  );
  const q = normalizarParaBuscar(texto.trim());
  const coincidencias = useMemo(() => {
    if (!q) return [];
    return categorias
      .map((c) => ({ c, partes: partesDelCamino(categorias, c.id) }))
      .filter(({ partes }) =>
        normalizarParaBuscar(partes.join(" ")).includes(q),
      )
      .sort(
        (a, b) =>
          a.partes.length - b.partes.length ||
          a.partes.join().localeCompare(b.partes.join(), "es"),
      );
  }, [categorias, q]);
  const partes = partesEscritas(texto);
  const { madreId, faltan } = resolverCamino(categorias, partes);
  const yaExiste = partes.length > 0 && faltan.length === 0;

  /** Crea el camino escrito ("Remeras › Oversize" crea lo que falte) y elige la última. */
  const crearCamino = async () => {
    if (!onCrear || creando || partes.length === 0) return;
    if (yaExiste) {
      if (madreId) onElegir(madreId);
      return;
    }
    setCreando(true);
    setError(null);
    let madre = madreId;
    for (const nombre of faltan) {
      const r = await onCrear(nombre, madre);
      if (r.error || !r.id) {
        setError(r.error ?? "No se pudo crear la categoría");
        setCreando(false);
        return;
      }
      madre = r.id;
    }
    setCreando(false);
    if (madre) onElegir(madre);
  };

  const crearSubcategoria = async (madre: string) => {
    const nombre = nombreNueva.trim();
    if (!onCrear || !nombre || creando) return;
    if (hermanaRepetida(categorias, madre, nombre)) {
      setError("Ya hay una subcategoría con ese nombre ahí");
      return;
    }
    setCreando(true);
    setError(null);
    const r = await onCrear(nombre, madre);
    setCreando(false);
    if (r.error || !r.id) {
      setError(r.error ?? "No se pudo crear la subcategoría");
      return;
    }
    onElegir(r.id);
  };

  const plegar = (cid: string) =>
    setPlegadas((p) => {
      const s = new Set(p);
      if (s.has(cid)) s.delete(cid);
      else s.add(cid);
      return s;
    });

  const filaElegible = (esta: boolean) =>
    `flex min-w-0 flex-1 items-center gap-2 py-2 text-left text-[14px] ${
      esta ? "font-medium text-brand-primary" : "text-[#16181D]"
    }`;

  return (
    // Buscador y pie fijos; sólo la lista scrollea (el panel tiene `scrollPropio`).
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 border-b border-black/[0.06] p-2">
        <div className="relative">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-black/35"
          />
          <input
            ref={buscador}
            type="text"
            value={texto}
            onChange={(e) => {
              setTexto(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              e.preventDefault();
              // Una sola coincidencia y sin camino escrito: Enter la elige. Si no, crea lo escrito.
              if (coincidencias.length === 1 && !/[›>/]/.test(texto))
                onElegir(coincidencias[0].c.id);
              else void crearCamino();
            }}
            placeholder={
              onCrear ? "Buscar o crear una categoría" : "Buscar una categoría"
            }
            aria-label={
              onCrear ? "Buscar o crear una categoría" : "Buscar una categoría"
            }
            className="h-9 w-full rounded-full bg-black/[0.04] pl-9 pr-3 text-[14px] text-[#16181D] outline-none placeholder:text-black/40 focus:bg-white focus:ring-2 focus:ring-[rgb(var(--brand-primary-rgb)_/_0.085)]"
          />
        </div>
      </div>

      <div
        className="min-h-0 flex-1 overflow-y-auto p-1.5"
        role="listbox"
        aria-label="Categorías"
      >
        {cargando && categorias.length === 0 ? (
          <p className="px-3 py-4 text-center text-[13px] text-black/45">
            Cargando…
          </p>
        ) : q ? (
          <>
            {coincidencias.map(({ c, partes: camino }) => (
              <button
                key={c.id}
                type="button"
                role="option"
                aria-selected={c.id === elegida}
                onClick={() => onElegir(c.id)}
                className="flex w-full items-center gap-2 rounded-lg px-3 hover:bg-black/[0.04]"
              >
                <span className={filaElegible(c.id === elegida)}>
                  <Camino partes={camino} />
                </span>
                {c.id === elegida && (
                  <Check
                    aria-hidden
                    className="h-4 w-4 shrink-0 text-brand-primary"
                  />
                )}
              </button>
            ))}
            {onCrear && !yaExiste && partes.length > 0 && (
              <button
                type="button"
                disabled={creando}
                onClick={() => void crearCamino()}
                className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[14px] font-medium text-brand-primary hover:bg-[rgb(var(--brand-primary-rgb)_/_0.045)] disabled:opacity-50"
              >
                <Plus className="h-4 w-4 shrink-0" />
                <span className="min-w-0 truncate">
                  Crear «{partes.join(" › ")}»
                </span>
              </button>
            )}
          </>
        ) : (
          <>
            {extras.map((x) => (
              <button
                key={x.id}
                type="button"
                role="option"
                aria-selected={x.id === elegida}
                onClick={() => onElegir(x.id)}
                className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-[14px] ${
                  x.id === elegida
                    ? "bg-[rgb(var(--brand-primary-rgb)_/_0.085)] font-medium text-brand-primary"
                    : "text-[#16181D] hover:bg-black/[0.04]"
                }`}
              >
                <span className="min-w-0 flex-1 truncate">{x.etiqueta}</span>
                {x.id === elegida && (
                  <Check
                    aria-hidden
                    className="h-4 w-4 shrink-0 text-brand-primary"
                  />
                )}
              </button>
            ))}
            {extras.length > 0 && filas.length > 0 && (
              <div aria-hidden className="mx-2 my-1.5 h-px bg-black/[0.06]" />
            )}
            {filas.length === 0 ? (
              <p className="px-3 py-4 text-center text-[13px] leading-snug text-black/45">
                {onCrear
                  ? "Todavía no tenés categorías. Escribí una arriba para crearla; con «›» armás una subcategoría: Remeras › Oversize."
                  : "Todavía no tenés categorías."}
              </p>
            ) : (
              filas.map(
                ({ categoria: c, nivel, tieneHijas, esUltima, guias }, i) => {
                  const esta = c.id === elegida;
                  const plegada = plegadas.has(c.id);
                  return (
                    <React.Fragment key={c.id}>
                      {/* Cada categoría de primer nivel abre un grupo, separado del anterior por una línea. */}
                      {nivel === 0 && i > 0 && (
                        <div
                          aria-hidden
                          className="mx-2 my-1.5 h-px bg-black/[0.06]"
                        />
                      )}
                      {/* Toda la fila elige; la flechita (a la derecha del nombre) y el "+" no. */}
                      <div
                        onClick={() => onElegir(c.id)}
                        className={`group relative flex cursor-pointer items-center gap-0.5 rounded-lg pr-1 ${esta ? "bg-[rgb(var(--brand-primary-rgb)_/_0.085)]" : "hover:bg-black/[0.04]"}`}
                        style={{ paddingLeft: INICIO + nivel * NIVEL }}
                      >
                        <LineasDeLaFila
                          nivel={nivel}
                          guias={guias}
                          esUltima={esUltima}
                          ancho={NIVEL}
                          inicio={INICIO}
                        />
                        <button
                          type="button"
                          role="option"
                          aria-selected={esta}
                          onClick={(e) => {
                            e.stopPropagation();
                            onElegir(c.id);
                          }}
                          className={`flex min-w-0 items-center py-2 text-left text-[14px] ${
                            esta
                              ? "font-medium text-brand-primary"
                              : "text-[#16181D]"
                          } ${nivel === 0 ? "font-semibold" : ""}`}
                        >
                          <span className="min-w-0 truncate">{c.nombre}</span>
                        </button>
                        {tieneHijas && (
                          <BotonDePlegar
                            plegada={plegada}
                            onClick={() => plegar(c.id)}
                          />
                        )}
                        <span className="flex-1" />
                        {esta && (
                          <Check
                            aria-hidden
                            className="h-4 w-4 shrink-0 text-brand-primary"
                          />
                        )}
                        {onCrear && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSubcategoriaEn(c.id);
                              setNombreNueva("");
                              setError(null);
                            }}
                            title={`Nueva subcategoría en ${c.nombre}`}
                            aria-label={`Nueva subcategoría en ${c.nombre}`}
                            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-black/35 transition-opacity hover:bg-white hover:text-brand-primary focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:hover)]:opacity-0"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      {subcategoriaEn === c.id && (
                        <div
                          className="relative py-1 pr-1"
                          style={{ paddingLeft: INICIO + (nivel + 1) * NIVEL }}
                        >
                          <LineasDeLaFila
                            nivel={nivel + 1}
                            guias={[...guias, !esUltima]}
                            esUltima={!(tieneHijas && !plegada)}
                            ancho={NIVEL}
                            inicio={INICIO}
                          />
                          <input
                            autoFocus
                            type="text"
                            value={nombreNueva}
                            onChange={(e) => {
                              setNombreNueva(e.target.value);
                              setError(null);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                void crearSubcategoria(c.id);
                              }
                              if (e.key === "Escape") {
                                e.stopPropagation();
                                setSubcategoriaEn(null);
                              }
                            }}
                            onBlur={() =>
                              !nombreNueva.trim() && setSubcategoriaEn(null)
                            }
                            disabled={creando}
                            placeholder={`Nueva en ${c.nombre}`}
                            className="h-8 w-full rounded-full border border-brand-primary bg-white px-3 text-[13px] text-[#16181D] outline-none ring-2 ring-[rgb(var(--brand-primary-rgb)_/_0.085)]"
                          />
                        </div>
                      )}
                    </React.Fragment>
                  );
                },
              )
            )}
          </>
        )}
        {error && (
          <p className="px-3 py-2 text-[12.5px] text-error-text">{error}</p>
        )}
      </div>

      {onEditar && (
      <div className="shrink-0 border-t border-black/[0.06] p-1.5">
        <button
          type="button"
          onClick={onEditar}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-[13px] font-medium text-black/60 hover:bg-black/[0.04] hover:text-[#16181D]"
        >
          <Pencil className="h-3.5 w-3.5" />
          {etiquetaEditar}
        </button>
      </div>
      )}
    </div>
  );
}
